import {AddSchema, CreateAccessor, GetDoc, GetDocs, MGLClass} from "mobx-graphlink";
import {emptyArray} from "js-vextensions";
import {Answer, DoorDecision, DoorVerdict, EchoDoorOffer, EchoRoundStatus, GeneratedBullet, JudgeAttempt, JudgeLoopSummary, RoundSlot} from "../../../EchoHunt/Types.js";

export const GetEchoRound = CreateAccessor((id: string|n): EchoRound|n=>{
	if (id == null) return null;
	return GetDoc({}, a=>a.echoRounds.get(id));
});
export const GetEchoRounds = CreateAccessor((userID: string|n): EchoRound[]=>{
	if (userID == null) return emptyArray; // if we aren't signed-in, don't even try to make request (the security-rules will make it fail)
	return GetDocs({params: {filter: {creator: userID && {equalTo: userID}}}}, a=>a.echoRounds);
});
export const GetEchoRoundsForStory = CreateAccessor((storyID: string|n): EchoRound[]=>{
	if (storyID == null) return emptyArray;
	return GetDocs({params: {filter: {storyId: {equalTo: storyID}}}}, a=>a.echoRounds).OrderBy(a=>a.createdAt);
});

/** One row per round (§14 `echo_rounds`).
 *
 * IMPORTANT (invariant #5): this is the *only* place generated material is stored. Fakes -- including
 * rejected candidates -- live in `generatedBullets` here, and must never reach the corpus tables. */
@MGLClass({table: "echoRounds"})
export class EchoRound {
	constructor(data?: Partial<EchoRound>) { Object.assign(this, data); }

	id: string;
	creator: string;
	createdAt: number;

	/** the character run this round belongs to (§8.3) */
	storyId?: string|n;

	status?: EchoRoundStatus|n;

	/** the round's settings, snapshotted so old rounds stay interpretable after settings change (§11, §13.1) */
	settings?: any|n;
	/** share of bullets that were real for this round (§9.1) */
	xShare?: number|n;
	/** min miscategorization target used for this round (§9.1) */
	yTarget?: number|n;
	/** were the shared-reshape seed words used this round? (§6 "unseeded rounds" mitigation) */
	seedingEnabled?: boolean|n;

	worldId?: string|n;
	characterId?: string|n;
	frame?: string|n;

	/** story summary coming into this round (§8.1) */
	storylineSummaryIn?: string|n;
	/** story summary produced by the weaver for the next round (§8.1) */
	storylineSummaryOut?: string|n;
	/** §11.5 W3: the way out the weaver offered, INCLUDING the world it leads to. The target is fixed here,
	 * at weave time, so the description can name the far side honestly and resolution is a lookup rather
	 * than a fresh random draw. */
	doorOffer?: EchoDoorOffer|n;
	/** §11.5 W2: the PLAYER's verdict on the offer, recorded at the reveal. Authorial, outside the fiction:
	 * the character has not seen the door. Null/absent = not yet answered. `accept` stages the door for the
	 * next round; `decline` drops it. Moves nothing on its own. */
	doorVerdict?: DoorVerdict|n;
	/** §11.5 W2: the CHARACTER's decision, written by round generation when a staged door is present.
	 * Only `taken` appends a world visit, and the move takes effect in the FOLLOWING round. */
	doorDecision?: DoorDecision|n;
	/** §11.5 W7: where the door actually led, recorded when the decision was applied. Lets the round log
	 * distinguish a door move from a manual one without reading the story history. */
	doorResolvedWorldId?: string|n;
	doorResolvedAt?: number|n;

	/** the woven slots; `sourceKind` here is label data and must not reach the hunt UI early (§16.0) */
	slots?: RoundSlot[]|n;
	/** the user's marks (§10). Written progressively while playing, so a reload restores the round
	 * mid-way (P9); `revealedAt` is what separates "in progress" from "already revealed". */
	answers?: Answer[]|n;
	/** when the round was submitted and its answers revealed (P9). Null/absent = still in progress.
	 * A separate field rather than a status value because `status` belongs to the JUDGE loop (§9.4):
	 * a revealed round is still `unjudged` until P10 has run. */
	revealedAt?: number|n;
	/** every judge attempt from the repair loop (§9) */
	judgeLog?: JudgeAttempt[]|n;
	/** P10: what the judge loop concluded, for the reveal and the §13.3 stats. Labels live here, so it is
	 * only ever SHOWN after the reveal. */
	judgeSummary?: JudgeLoopSummary|n;
	/** the fakes generated for this round, including rejected candidates (§5) */
	generatedBullets?: GeneratedBullet[]|n;

	/** run-wide token/call totals, attached for the §13.1 round log (P3) */
	outputTokensTotal?: number|n;
	llmCallsTotal?: number|n;
}
AddSchema("EchoRound", {
	properties: {
		id: {type: "string"},
		creator: {type: "string"},
		createdAt: {type: "number"},

		storyId: {type: ["null", "string"]},

		status: {oneOf: [{type: "null"}, {$ref: "EchoRoundStatus"}]},

		settings: {type: ["null", "object"]},
		xShare: {type: ["null", "number"]},
		yTarget: {type: ["null", "number"]},
		seedingEnabled: {type: ["null", "boolean"]},

		worldId: {type: ["null", "string"]},
		characterId: {type: ["null", "string"]},
		frame: {type: ["null", "string"]},

		storylineSummaryIn: {type: ["null", "string"]},
		storylineSummaryOut: {type: ["null", "string"]},
		doorOffer: {oneOf: [{type: "null"}, {$ref: "EchoDoorOffer"}]},
		doorVerdict: {oneOf: [{type: "null"}, {$ref: "DoorVerdict"}]},
		doorDecision: {oneOf: [{type: "null"}, {$ref: "DoorDecision"}]},
		doorResolvedWorldId: {type: ["null", "string"]},
		doorResolvedAt: {type: ["null", "number"]},

		slots: {oneOf: [{type: "null"}, {items: {$ref: "RoundSlot"}}]},
		answers: {oneOf: [{type: "null"}, {items: {$ref: "Answer"}}]},
		revealedAt: {type: ["null", "number"]},
		judgeLog: {oneOf: [{type: "null"}, {items: {$ref: "JudgeAttempt"}}]},
		judgeSummary: {oneOf: [{type: "null"}, {$ref: "JudgeLoopSummary"}]},
		generatedBullets: {oneOf: [{type: "null"}, {items: {$ref: "GeneratedBullet"}}]},

		outputTokensTotal: {type: ["null", "number"]},
		llmCallsTotal: {type: ["null", "number"]},
	},
});
